import { ArtifactType } from "@/enums/ArtifactType";
import api from "@/api";
import { auth } from "@/auth";
import type { Label } from "@/types/Label";
import type { ToastInterface } from "vue-toastification";
import { copyLinkToClipboard } from "./clipboard";
import { calculateTimeout } from "./utilities";
import { logError } from "./logError";

/**
 * The toast methods shareArtifact actually uses. Previously `any`, which made the shared
 * helper's typed handler a no-op at this boundary — the two call sites
 * (EntityDetailPage.vue, search/SearchResults.vue) got no checking at all.
 */
export type ShareArtifactToastHandler = Pick<ToastInterface, "success" | "info" | "error" | "dismiss">;

export interface Artifact {
	id: string;
	conversationId?: string;
}

interface ArtifactTypeConfig {
	singularName: string;
	pluralName: string;
}

const artifactConfigs: Record<ArtifactType, ArtifactTypeConfig> = {
	[ArtifactType.Snapshots]: {
		singularName: "photo",
		pluralName: "photos"
	},
	[ArtifactType.Files]: {
		singularName: "document",
		pluralName: "documents"
	},
	[ArtifactType.Recordings]: {
		singularName: "video",
		pluralName: "videos"
	},
	[ArtifactType.Content]: {
		singularName: "media item",
		pluralName: "media items"
	}
};

export function getArtifactTypeConfig(artifactType: ArtifactType): ArtifactTypeConfig {
	return artifactConfigs[artifactType];
}

export async function deleteArtifacts(artifactType: ArtifactType, selectedIds: string[], artifacts: Artifact[]): Promise<Artifact[]> {
	if (selectedIds.length === 0) {
		return artifacts;
	}

	const artifactsToDelete = artifacts
		.filter((x) => selectedIds.includes(x.id))
		.map((x) => ({ id: x.id, conversationId: x.conversationId }));

	await api.deleteArtifacts(artifactType, artifactsToDelete);
	return artifacts.filter((x) => !selectedIds.includes(x.id));
}

/**
 * Bulk apply tags to artifacts using the bulk-actions API.
 * - For Recordings, include conversationId on each artifact as used elsewhere.
 * - Operation can be 'add' (append) or 'replace' (overwrite existing tags).
 */
export async function tagArtifacts(
	artifactType: ArtifactType,
	selectedIds: string[],
	artifacts: Artifact[],
	tags: Label[],
	operation: "add" | "replace" = "add"
) {
	const artifactsToTag = artifacts.filter((x) => selectedIds.includes(x.id)).map((x) => ({ id: x.id, conversationId: x.conversationId }));

	return await (api as any).addTagsToArtifacts(artifactType, artifactsToTag, tags, operation);
}

export async function shareArtifact(artifactId: string, type: ArtifactType, context: any, toastHandler: ShareArtifactToastHandler) {
	// Optional-chained on purpose: an unrecognised type yields no config, and reading .singularName
	// off undefined would throw before any try block could report it.
	const artifactName = getArtifactTypeConfig(type)?.singularName ?? "share";

	/**
	 * The single reporting path for every failure below. The toast call is itself guarded: if
	 * reporting throws, a handled failure would otherwise become a silent one again — the exact
	 * class LSP-18938 exists to remove.
	 */
	const reportFailure = (summary: string, cause: unknown) => {
		logError(`[CopyLink] ${summary}`, cause, { handler: "shareArtifact", artifactId, artifactType: type });

		const message = `Something went wrong getting the ${artifactName} link. Please try again.`;
		try {
			toastHandler.error(message, { timeout: calculateTimeout(message) });
		} catch (toastError) {
			logError("[CopyLink] Could not show the share-link error toast.", toastError, {
				handler: "shareArtifact",
				artifactId,
				artifactType: type
			});
		}
	};

	// Everything is inside the try, including building the URL promise. Several steps here can throw
	// SYNCHRONOUSLY — `context.recordings.find(...)` returning undefined makes the next property read
	// a TypeError, and an api adapter may throw before returning a promise. Outside the try those
	// escaped as an unhandled rejection with no toast at all (break-it delta round).
	try {
		// Build the share-URL fetcher synchronously so the clipboard.write() call below can start
		// inside the original user gesture. Safari consumes the user-activation token on the first
		// await, which is why the legacy `await fetchUrl()` then `copyToClipboard` flow silently
		// failed on Safari (LSP-15622).
		let urlPromise: Promise<string> | null = null;

		if (type === ArtifactType.Recordings) {
			let meetingId, conversationId;

			if (context.conversation) {
				// ConversationPage context
				meetingId = context.conversation.meetingId;
				conversationId = context.conversation.id;
			} else {
				// ProjectPage context
				const recording = context.recordings.find((x: any) => x.id == artifactId);
				conversationId = recording.conversationId;
				const recordingConversation = context.recordingConversations.find((x: any) => x.id == conversationId);
				meetingId = recordingConversation.meetingId;
			}

			urlPromise = api
				.getRecordingShareUrl({
					recordingId: artifactId,
					meetingId,
					conversationId
				})
				.then((response: any) => response.url);
		} else if (type === ArtifactType.Snapshots) {
			let conversationId;

			if (context.conversation) {
				conversationId = context.conversation.id;
			} else {
				const snapshot = context.snapshots.find((x: any) => x.id == artifactId);
				conversationId = snapshot.conversationId;
			}

			urlPromise = api
				.getSnapshotShareUrl({
					snapshotId: artifactId,
					conversationId
				})
				.then((response: any) => response.url);
		}

		if (!urlPromise) {
			// Previously a bare console.warn with no toast, so the click did nothing visible. Flagged
			// as pre-existing by ls-heroku-ci and again by the break-it delta round; fixed here since
			// it is the same silent-failure class in a function this change already rewrites.
			reportFailure(
				`Unsupported artifact type "${type}"; no share URL can be built.`,
				new Error(`Unsupported artifact type "${type}".`)
			);
			return;
		}

		// Delegate the Safari user-activation workaround and the manual-copy fallback to the shared
		// helper (see helpers/clipboard.ts).
		//
		// The helper rethrows when the URL cannot be obtained or is unusable. This function used to
		// propagate that to the caller, but no caller ever handled it: both `share()` handlers
		// (EntityDetailPage.vue and search/SearchResults.vue) `await shareArtifact(...)` bare, there
		// is no app.config.errorHandler or unhandledrejection net, and Vue does not surface a
		// rejected async event handler as UI. So the "propagate to the caller" contract produced a
		// silent failure. Report it here instead, where the toastHandler is already in hand.
		await copyLinkToClipboard({
			urlPromise,
			noun: artifactName,
			toast: toastHandler
		});
	} catch (shareError) {
		reportFailure(`Failed to obtain a usable share URL for a ${artifactName}.`, shareError);
	}
}
